// components/DetailAnnonce.js
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './DetailAnnonce.css';
import { useCurrency } from '../utils/currency';
import ImageGallery from './common/ImageGallery';
import { ouvrirConversation } from '../utils/messagerie';
import PriceHistoryChart from './common/PriceHistoryChart';
import { useI18n } from '../i18n/I18nContext';

// ── Banques algériennes (taux 2025) ──────────────────────────────────────────
const BANQUES_CREDIT = [
  { id: 'cpa',   nom: 'CPA',        taux: 6.50, icon: '🏦' },
  { id: 'cnep',  nom: 'CNEP Banque',taux: 6.75, icon: '🏦' },
  { id: 'bna',   nom: 'BNA',        taux: 6.75, icon: '🏦' },
  { id: 'bonif', nom: 'Bonifié *',  taux: 1.00, icon: '🏛️', special: true },
];

function calcMens(montant, tauxAnnuel, dureeAns) {
  if (montant <= 0) return 0;
  if (tauxAnnuel === 0) return montant / (dureeAns * 12);
  const r = tauxAnnuel / 100 / 12;
  const n = dureeAns * 12;
  return montant * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function SimulationCredit({ prix }) {
  const [apportPct, setApportPct] = React.useState(20);
  const [duree, setDuree] = React.useState(20);

  const apport   = Math.round(prix * apportPct / 100);
  const montant  = prix - apport;

  const fmt = v => Math.round(v).toLocaleString('fr-DZ') + ' DA';

  return (
    <div className="sim-credit">
      {/* En-tête */}
      <div className="sim-credit-hdr">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="5" width="20" height="14" rx="2"/>
          <line x1="2" y1="10" x2="22" y2="10"/>
          <line x1="6"  y1="15" x2="10" y2="15"/>
        </svg>
        <h3>Simulation de crédit immobilier</h3>
      </div>

      {/* Contrôles */}
      <div className="sim-controls">
        {/* Apport */}
        <div className="sim-ctrl-group">
          <div className="sim-ctrl-label">
            <span>Apport personnel</span>
            <strong>{apportPct}% — {fmt(apport)}</strong>
          </div>
          <input
            type="range" min="10" max="50" step="5"
            value={apportPct}
            onChange={e => setApportPct(Number(e.target.value))}
            className="sim-slider"
            style={{ '--pct': `${(apportPct - 10) / (50 - 10) * 100}%` }}
          />
          <div className="sim-slider-ticks">
            {[10,15,20,25,30,35,40,45,50].map(v => (
              <span key={v} className={v === apportPct ? 'active' : ''}>{v}%</span>
            ))}
          </div>
        </div>

        {/* Durée */}
        <div className="sim-ctrl-group">
          <span className="sim-ctrl-label"><span>Durée du crédit</span></span>
          <div className="sim-duree-btns">
            {[10, 15, 20, 25].map(d => (
              <button
                key={d}
                className={`sim-duree-btn${duree === d ? ' active' : ''}`}
                onClick={() => setDuree(d)}
              >
                {d} ans
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Montant emprunté */}
      <div className="sim-montant-row">
        <span>Montant emprunté</span>
        <strong className="sim-montant-val">{fmt(montant)}</strong>
      </div>

      {/* Tableau des banques */}
      <div className="sim-banks">
        {BANQUES_CREDIT.map(b => {
          const mens  = calcMens(montant, b.taux, duree);
          const total = Math.round(mens * duree * 12);
          const interets = total - montant;
          return (
            <div key={b.id} className={`sim-bank-card${b.special ? ' sim-bank-special' : ''}`}>
              <div className="sim-bank-name">
                <span className="sim-bank-icon">{b.icon}</span>
                {b.nom}
              </div>
              <div className="sim-bank-taux">{b.taux}% / an</div>
              <div className="sim-bank-mensualite">
                {fmt(Math.round(mens))}
                <span className="sim-bank-period">/mois</span>
              </div>
              <div className="sim-bank-total">
                Coût total : {fmt(total)}
                <span className="sim-bank-interets"> (+{fmt(interets)} intérêts)</span>
              </div>
            </div>
          );
        })}
      </div>

      <p className="sim-disclaimer">
        * Taux bonifié sous conditions FGCMPI (revenus ≤ 6×SNMG). Simulations à titre indicatif.
        Consultez votre conseiller bancaire pour un devis personnalisé.
      </p>
    </div>
  );
}

// ── Barre de partage social ───────────────────────────────────────────────────
function ShareBar({ titre, prix }) {
  const [copied, setCopied] = React.useState(false);
  const url = window.location.href;
  const text = encodeURIComponent(`${titre} — ${prix}\n${url}`);
  const urlEnc = encodeURIComponent(url);

  const copyLink = () => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  };

  const nativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: titre, text: `${titre} — ${prix}`, url });
    } else {
      copyLink();
    }
  };

  return (
    <div className="share-bar">
      <span className="share-bar-label">Partager :</span>

      {/* WhatsApp */}
      <a
        href={`https://wa.me/?text=${text}`}
        target="_blank" rel="noopener noreferrer"
        className="share-btn share-btn-wa"
        title="Partager sur WhatsApp"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        WhatsApp
      </a>

      {/* Facebook Messenger */}
      <a
        href={`https://www.facebook.com/dialog/send?link=${urlEnc}&app_id=966242223397468&redirect_uri=${urlEnc}`}
        target="_blank" rel="noopener noreferrer"
        className="share-btn share-btn-messenger"
        title="Partager sur Messenger"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.652V24l4.088-2.242c1.092.3 2.246.464 3.443.464 6.627 0 12-4.975 12-11.111S18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z"/>
        </svg>
        Messenger
      </a>

      {/* Facebook */}
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${urlEnc}`}
        target="_blank" rel="noopener noreferrer"
        className="share-btn share-btn-fb"
        title="Partager sur Facebook"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
        Facebook
      </a>

      {/* Copier le lien */}
      <button
        className={`share-btn share-btn-copy${copied ? ' copied' : ''}`}
        onClick={copyLink}
        title="Copier le lien"
      >
        {copied ? (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Copié !
          </>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
            </svg>
            Copier
          </>
        )}
      </button>

      {/* Share natif (mobile) */}
      {typeof navigator !== 'undefined' && navigator.share && (
        <button className="share-btn share-btn-native" onClick={nativeShare} title="Autres options">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          Plus
        </button>
      )}
    </div>
  );
}

function DetailAnnonce({ annonce, similaires = [], onRetour, onSupprimer, onVoirDetail }) {
  const { formatPrix } = useCurrency();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [guideTab, setGuideTab] = useState('etapes');
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgTexte, setMsgTexte] = useState('');
  const [msgEnvoye, setMsgEnvoye] = useState(false);
  const msgTextareaRef = useRef(null);

  const envoyerPremierMessage = () => {
    if (!msgTexte.trim()) return;
    const convId = ouvrirConversation(annonce);
    // Ajouter le premier message
    try {
      const convs = JSON.parse(localStorage.getItem('darna-immo_messages') || '[]');
      const updated = convs.map(c => c.id === convId
        ? { ...c, messages: [...c.messages, { id: Date.now(), texte: msgTexte.trim(), moi: true, date: new Date().toISOString() }] }
        : c
      );
      localStorage.setItem('darna-immo_messages', JSON.stringify(updated));
    } catch {}
    setMsgEnvoye(true);
    setTimeout(() => {
      navigate('/messages');
    }, 1200);
  };

  // Simulate interested users (pseudo-random based on annonce id for consistency)
  const getInterestedCount = () => {
    const seed = annonce.id ? annonce.id.toString().split('').reduce((a, b) => a + b.charCodeAt(0), 0) : 5;
    return 3 + (seed % 12);
  };

  const getPrixParM2 = () => {
    if (!annonce.surface || annonce.surface <= 0) return null;
    return Math.round(annonce.prix / annonce.surface).toLocaleString('fr-DZ');
  };

  const formatDate = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="detail-container">
      {/* BOUTON RETOUR */}
      <button className="btn btn-back" onClick={onRetour}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
        {t.annonce.back}
      </button>

      <div className="detail-content">
        {/* GALERIE PHOTOS */}
        <div className="detail-gallery-wrap">
          <ImageGallery
            images={
              annonce.photos && annonce.photos.length > 0
                ? annonce.photos
                : annonce.photo
                  ? [annonce.photo]
                  : []
            }
            alt={annonce.titre}
          />
          <div className="detail-gallery-badges">
            <span className="detail-type-badge">{annonce.type}</span>
            <span className="detail-verified">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
              {t.annonce.verified}
            </span>
          </div>
        </div>

        {/* INFORMATIONS PRINCIPALES */}
        <div className="detail-info">
          <div className="detail-header">
            {/* Viewers indicator */}
            <span className="detail-viewers">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              {getInterestedCount()} personnes consultent cette annonce
            </span>
            <h1>{annonce.titre}</h1>
            <div className="detail-price">
              {formatPrix(annonce.prix)}
            </div>
            <div className="detail-price-meta">
              <span className="detail-price-note">Prix affiché par l'annonceur</span>
              {getPrixParM2() && (
                <span className="detail-price-m2">{t.annonce.priceM2} {getPrixParM2()}</span>
              )}
            </div>
          </div>

          {/* CARACTÉRISTIQUES */}
          <div className="detail-specs">
            <div className="spec">
              <div className="spec-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </div>
              <span className="spec-label">Quartier</span>
              <span className="spec-value">{annonce.quartier}</span>
            </div>
            {annonce.surface > 0 && (
              <div className="spec">
                <div className="spec-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  </svg>
                </div>
                <span className="spec-label">Surface</span>
                <span className="spec-value">{annonce.surface} m²</span>
              </div>
            )}
            {annonce.chambres > 0 && (
              <div className="spec">
                <div className="spec-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 7v11a2 2 0 002 2h14a2 2 0 002-2V7"></path>
                    <path d="M21 7H3l2-4h14l2 4z"></path>
                  </svg>
                </div>
                <span className="spec-label">Chambres</span>
                <span className="spec-value">{annonce.chambres}</span>
              </div>
            )}
            {annonce.sallesEau > 0 && (
              <div className="spec">
                <div className="spec-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
                    <line x1="4" y1="22" x2="4" y2="15"></line>
                  </svg>
                </div>
                <span className="spec-label">Salles d'eau</span>
                <span className="spec-value">{annonce.sallesEau}</span>
              </div>
            )}
            {annonce.etage != null && (
              <div className="spec">
                <div className="spec-icon">🏢</div>
                <span className="spec-label">Étage</span>
                <span className="spec-value">
                  {annonce.etage === 0 ? 'RDC' : `${annonce.etage}${annonce.nb_etages ? `/${annonce.nb_etages}` : ''}`}
                </span>
              </div>
            )}
            {annonce.annee_construction && (
              <div className="spec">
                <div className="spec-icon">🏗️</div>
                <span className="spec-label">Construction</span>
                <span className="spec-value">{annonce.annee_construction}</span>
              </div>
            )}
            {annonce.etat && (
              <div className="spec">
                <div className="spec-icon">✨</div>
                <span className="spec-label">État</span>
                <span className="spec-value">
                  {{ neuf: 'Neuf', bon_etat: 'Bon état', a_renover: 'À rénover' }[annonce.etat] || annonce.etat}
                </span>
              </div>
            )}
            {annonce.orientation && (
              <div className="spec">
                <div className="spec-icon">🧭</div>
                <span className="spec-label">Orientation</span>
                <span className="spec-value">
                  {{ nord: 'Nord', sud: 'Sud', est: 'Est', ouest: 'Ouest' }[annonce.orientation] || annonce.orientation}
                </span>
              </div>
            )}
            <div className="spec">
              <div className="spec-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </div>
              <span className="spec-label">Publié le</span>
              <span className="spec-value">{formatDate(annonce.dateCreation || annonce.created_at)}</span>
            </div>
          </div>

          {/* ÉQUIPEMENTS */}
          {(annonce.parking || annonce.balcon || annonce.ascenseur || annonce.meuble || annonce.gardien) && (
            <div className="detail-equipements">
              <h2>Équipements</h2>
              <div className="equipements-tags">
                {annonce.parking   && <span className="eq-tag">🚗 Parking / Garage</span>}
                {annonce.balcon    && <span className="eq-tag">🌿 Balcon / Terrasse</span>}
                {annonce.ascenseur && <span className="eq-tag">🛗 Ascenseur</span>}
                {annonce.meuble    && <span className="eq-tag">🛋️ Meublé</span>}
                {annonce.gardien   && <span className="eq-tag">🔒 Gardien</span>}
              </div>
            </div>
          )}

          {/* DESCRIPTION */}
          <div className="detail-description">
            <h2>Description du bien</h2>
            <p>{annonce.description}</p>
          </div>

          {/* CONTACT */}
          <div className="detail-contact">
            <h2>{t.annonce.contact}</h2>
            <p className="contact-subtitle">Intéressé par ce bien ? Contactez directement le vendeur.</p>
            <div className="contact-buttons">
              {/* Messagerie in-app — action principale */}
              <button
                className="btn btn-contact btn-message-inapp"
                onClick={() => { setMsgOpen(o => !o); setTimeout(() => msgTextareaRef.current?.focus(), 100); }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                </svg>
                {t.annonce.message}
              </button>

              {/* Téléphone + WhatsApp côte à côte */}
              <div className="contact-secondary">
                {annonce.telephone && (
                  <a href={`tel:${annonce.telephone}`} className="btn btn-contact btn-phone">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"></path>
                    </svg>
                    {t.annonce.call}
                  </a>
                )}
                {annonce.telephone && (
                  <a
                    href={`https://wa.me/213${annonce.telephone.replace(/^0/, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-contact btn-whatsapp"
                  >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </a>
                )}
              </div>

              {/* Email — lien texte discret */}
              {annonce.email && (
                <a href={`mailto:${annonce.email}`} className="contact-email-link">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  Envoyer un email
                </a>
              )}
            </div>

            {/* Mini-messagerie inline */}
            {msgOpen && (
              <div className="contact-msg-inline">
                {msgEnvoye ? (
                  <div className="contact-msg-sent">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Message envoyé ! Redirection vers la messagerie…
                  </div>
                ) : (
                  <>
                    <p className="contact-msg-label">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        <polyline points="9 12 11 14 15 10"/>
                      </svg>
                      Échange sécurisé · visible uniquement par vous et le vendeur
                    </p>
                    <textarea
                      ref={msgTextareaRef}
                      className="contact-msg-textarea"
                      rows="3"
                      placeholder={`Bonjour, je suis intéressé(e) par "${annonce.titre}". Pouvez-vous me donner plus d'informations ?`}
                      value={msgTexte}
                      onChange={e => setMsgTexte(e.target.value)}
                    />
                    <div className="contact-msg-actions">
                      <button
                        className="contact-msg-send"
                        onClick={envoyerPremierMessage}
                        disabled={!msgTexte.trim()}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                        </svg>
                        Envoyer le message
                      </button>
                      <button className="contact-msg-cancel" onClick={() => setMsgOpen(false)}>Annuler</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* SÉCURITÉ */}
          <div className="detail-safety">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              <polyline points="9 12 11 14 15 10"></polyline>
            </svg>
            <span>{t.annonce.verified} — Restez vigilant lors de vos transactions.</span>
          </div>

          {/* SIMULATION DE CRÉDIT */}
          {annonce.prix > 0 && (
            <SimulationCredit prix={Number(annonce.prix)} />
          )}

          {/* HISTORIQUE PRIX / m² */}
          {annonce.surface > 0 && annonce.prix > 0 && (
            <PriceHistoryChart
              commune={annonce.commune || annonce.quartier}
              quartier={annonce.quartier}
              prixM2={Math.round(Number(annonce.prix) / Number(annonce.surface))}
            />
          )}

          {/* GUIDE D'ACQUISITION */}
          <div className="guide-acquisition">
            <div className="guide-header">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
              </svg>
              <h3>{t.annonce.guide}</h3>
            </div>
            <p className="guide-subtitle">Tout ce que vous devez savoir pour acheter en Algérie</p>

            <div className="guide-tabs">
              {[
                { id: 'etapes', label: 'Étapes' },
                { id: 'documents', label: 'Documents' },
                { id: 'frais', label: 'Frais & fiscalité' },
              ].map(t => (
                <button key={t.id} className={`guide-tab ${guideTab === t.id ? 'active' : ''}`} onClick={() => setGuideTab(t.id)}>
                  {t.label}
                </button>
              ))}
            </div>

            {guideTab === 'etapes' && (
              <div className="guide-content">
                {[
                  { n: '01', titre: 'Visite & compromis', desc: 'Visitez le bien, négociez le prix, signez un compromis de vente avec arrhes (5–10% du prix). Vérifiez l\'identité du vendeur et le titre de propriété.', docs: ['CNI vendeur', 'Acte de propriété', 'Compromis signé'] },
                  { n: '02', titre: 'Vérification juridique', desc: 'Consultez le registre foncier (Conservation foncière) pour vérifier l\'absence d\'hypothèques, servitudes ou litiges en cours.', docs: ['Relevé cadastral', 'Certificat de non-opposition'] },
                  { n: '03', titre: 'Financement', desc: 'Si financement bancaire : déposez votre dossier de crédit (CNEP, CPA, BNA…). Depuis 2025, le règlement doit obligatoirement transiter par les banques.', docs: ['Bulletins de salaire', 'Relevés bancaires 6 mois', 'Compromis de vente'] },
                  { n: '04', titre: 'Acte authentique', desc: 'Rendez-vous chez le notaire avec toutes les parties. L\'acte est enregistré et la propriété est officiellement transférée.', docs: ['Acte de propriété vendeur', 'CNI des deux parties', 'Quittance de paiement'] },
                  { n: '05', titre: 'Enregistrement & mutation', desc: 'Le notaire dépose l\'acte à la Conservation foncière pour mutation. Vous recevrez votre titre de propriété définitif sous 30–90 jours.', docs: ['Acte authentique signé', 'Reçu des droits d\'enregistrement'] },
                ].map(step => (
                  <div key={step.n} className="guide-step">
                    <div className="guide-step-num">{step.n}</div>
                    <div className="guide-step-body">
                      <strong>{step.titre}</strong>
                      <p>{step.desc}</p>
                      <div className="guide-step-docs">
                        {step.docs.map(d => <span key={d} className="guide-doc-chip">{d}</span>)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {guideTab === 'documents' && (
              <div className="guide-content">
                <div className="guide-docs-grid">
                  <div className="guide-docs-col">
                    <h4>Acheteur</h4>
                    <ul>
                      <li>Carte nationale d'identité (2 copies)</li>
                      <li>Acte de naissance (fiche familiale)</li>
                      <li>Justificatif de domicile</li>
                      <li>3 derniers bulletins de salaire</li>
                      <li>Relevés bancaires (6 mois)</li>
                      <li>Extrait de casier judiciaire</li>
                    </ul>
                  </div>
                  <div className="guide-docs-col">
                    <h4>Vendeur</h4>
                    <ul>
                      <li>Acte de propriété original</li>
                      <li>Titre foncier ou acte authentique</li>
                      <li>Carte nationale d'identité</li>
                      <li>Relevé cadastral à jour</li>
                      <li>Quittances d'impôts fonciers</li>
                      <li>Certificat de non-opposition</li>
                    </ul>
                  </div>
                  <div className="guide-docs-col">
                    <h4>Bien immobilier</h4>
                    <ul>
                      <li>Plan de masse / coupe</li>
                      <li>Permis de construire</li>
                      <li>Certificat de conformité</li>
                      <li>Factures d'eau, gaz, électricité</li>
                    </ul>
                  </div>
                </div>
                <div className="guide-diaspora-note">
                  <strong>Diaspora & étrangers</strong>
                  <p>Les Algériens résidant à l'étranger doivent fournir un passeport valide et peuvent mandater un représentant légal via procuration notariée. Les étrangers non-résidents doivent obtenir une autorisation préalable du Ministère des Finances.</p>
                </div>
              </div>
            )}

            {guideTab === 'frais' && (
              <div className="guide-content">
                <div className="guide-frais-cards">
                  <div className="guide-frais-card">
                    <span className="frais-label">Frais de notaire</span>
                    <span className="frais-value">1 – 2%</span>
                    <span className="frais-desc">du prix de vente, à la charge de l'acheteur</span>
                  </div>
                  <div className="guide-frais-card">
                    <span className="frais-label">Droits d'enregistrement</span>
                    <span className="frais-value">5%</span>
                    <span className="frais-desc">taxe foncière sur la mutation, payée à l'enregistrement</span>
                  </div>
                  <div className="guide-frais-card">
                    <span className="frais-label">Honoraires agence</span>
                    <span className="frais-value">2 – 3%</span>
                    <span className="frais-desc">si passage par un intermédiaire immobilier</span>
                  </div>
                  <div className="guide-frais-card">
                    <span className="frais-label">Conservation foncière</span>
                    <span className="frais-value">1 – 1,5%</span>
                    <span className="frais-desc">frais de mutation et d'inscription au registre</span>
                  </div>
                </div>
                <div className="guide-fiscalite">
                  <h4>Fiscalité vendeur</h4>
                  <ul>
                    <li>Plus-value immobilière : 15% si cession dans les 10 ans de l'acquisition</li>
                    <li>Exonération après 10 ans de détention ou en cas de résidence principale</li>
                    <li>IRG sur la plus-value : déclaré lors de la transaction notariale</li>
                  </ul>
                </div>
                <div className="guide-bancaire-note">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <span><strong>Obligation bancaire 2025 :</strong> tout paiement immobilier doit transiter par le système bancaire algérien. Le règlement en espèces n'est plus légalement reconnu pour les transactions immobilières.</span>
                </div>
              </div>
            )}
          </div>

          {/* ANNONCES SIMILAIRES */}
          {similaires.length > 0 && (
            <div className="similaires-section">
              <h3 className="similaires-title">{t.annonce.similar}</h3>
              <div className="similaires-grid">
                {similaires.map(s => (
                  <div key={s.id} className="similaire-card" onClick={() => onVoirDetail && onVoirDetail(s.id)}>
                    <div className="similaire-img">
                      <img src={s.photo} alt={s.titre} />
                      <span className="similaire-badge">{s.type}</span>
                    </div>
                    <div className="similaire-info">
                      <p className="similaire-titre">{s.titre}</p>
                      <p className="similaire-prix">{formatPrix(s.prix)}</p>
                      <p className="similaire-loc">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                        </svg>
                        {s.quartier}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SHARE ROW */}
          <ShareBar titre={annonce.titre} prix={formatPrix(annonce.prix)} />

          {/* ACTIONS */}
          <div className="detail-actions">
            <button
              className="btn btn-danger"
              onClick={() => {
                if (window.confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) {
                  onSupprimer();
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
              </svg>
              {t.annonce.delete}
            </button>
          </div>
        </div>
      </div>

      {/* STICKY CONTACT BAR (MOBILE) */}
      {annonce.telephone && (
        <div className="sticky-contact-bar">
          <div className="sticky-contact-bar-inner">
            <a href={`tel:${annonce.telephone}`} className="btn-sticky btn-sticky-phone">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"></path>
              </svg>
              Appeler
            </a>
            <a
              href={`https://wa.me/213${annonce.telephone.replace(/^0/, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-sticky btn-sticky-whatsapp"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default DetailAnnonce;
